import React, { useState, useEffect } from 'react';

// Icon name to file path mapping (in public/icons folder)
const iconPaths = {
  'sidebar_home': '/icons/sidebar_home.svg',
  'sidebar_overview': '/icons/sidebar_overview.svg',
  'sidebar_activity': '/icons/sidebar_activity.svg',
  'agent': '/icons/agent.svg',
  'dashboard': '/icons/dashboard.svg',
  'sidebar_pinned': '/icons/sidebar_pinned.svg',
  'sidebar_supporticon': '/icons/sidebar_supporticon.svg',
  'sidebar_docs': '/icons/sidebar_docs.svg',
  'sidebar_changelog': '/icons/sidebar_changelog.svg',
  'sidebar_support': '/icons/sidebar_support.svg',
  'sidebar_contactsales': '/icons/sidebar_contactsales.svg',
  'sidebar_settings': '/icons/sidebar_settings.svg',
  'sidebar_usersettings': '/icons/sidebar_usersettings.svg',
  'sidebar_apps': '/icons/sidebar_apps.svg',
  'sidebar_smartcontract': '/icons/sidebar_smartcontract.svg',
  'sidebar_logout': '/icons/sidebar_logout.svg',
  'sidebar_collapsesidebar': '/icons/sidebar_collapsesidebar.svg',
  'calendar': '/icons/calendar.svg',
  'edit_pencil': '/icons/edit_pencil.svg',
  /** Agent / sidebar observe & assess (public/icons/*.svg) */
  overview: '/icons/overview.svg',
  event: '/icons/event.svg',
  user: '/icons/user.svg',
  timeline: '/icons/timeline.svg',
  trace: '/icons/trace.svg',
  conversation: '/icons/conversation.svg',
  ask: '/icons/ask.svg',
  span: '/icons/span.svg',
  generate: '/icons/generate.svg',
  retrieve: '/icons/retrieve.svg',
  usecase: '/icons/usecase.svg',
  issues: '/icons/issues.svg',
  eval: '/icons/eval.svg',
  'add_plus': '/icons/add_plus.svg',
  'save': '/icons/save.svg',
  'filter': '/icons/filter.svg',
  'linechart': '/icons/linechart.svg',
  'stacked_linechart': '/icons/stacked_linechart.svg',
  'barchart': '/icons/barchart.svg',
  'stacked_barchart': '/icons/stacked_barchart.svg',
  'piechart': '/icons/piechart.svg',
  'table': '/icons/table.svg',
  'metric': '/icons/metric.svg',
  'analytics': '/icons/analytics.svg',
  'funnel': '/icons/funnel.svg',
  'flows': '/icons/flows.svg',
  'retention': '/icons/retention.svg',
  'share': '/icons/share.svg',
  'text': '/icons/text.svg',
  'media': '/icons/media.svg',
  'delete': '/icons/delete.svg',
  'website': '/icons/website.svg',
  'smartcontract': '/icons/smartcontract.svg',
  'thumb_up': '/icons/thumb_up.svg',
  'thumb_down': '/icons/thumb_down.svg',
};

/**
 * Reusable Icon component that loads SVG icons from the public/icons folder
 * @param {string} name - Name of the icon (e.g., 'sidebar_home')
 * @param {number} size - Size of the icon in pixels (default: 24)
 * @param {string} color - Color of the icon (default: 'currentColor')
 * @param {object} style - Additional inline styles
 * @param {string} className - Additional CSS classes
 */
/**
 * Every icon, fetched once for the session.
 *
 * Each mount used to fetch its own copy, and render an <img> of the same file
 * while that was in flight — so one icon cost two requests, and the ten in the
 * sidebar cost twenty on every page load, again on every navigation. Measured
 * in production 2026-09-25: 20 requests for 10 distinct icons, the pairs 1ms
 * apart, one `img` and one `fetch`.
 *
 * `text` holds what came back; `inflight` holds the promise, so ten components
 * mounting together share one request instead of racing ten. A cache hit
 * renders inline on the first paint — no <img>, no second request, and no
 * flash of the uncoloured fallback.
 */
const text = new Map();
const inflight = new Map();

function loadIcon(iconPath) {
  if (text.has(iconPath)) return Promise.resolve(text.get(iconPath));
  if (inflight.has(iconPath)) return inflight.get(iconPath);
  const p = fetch(iconPath)
    .then((res) => {
      if (!res.ok) throw new Error(`Failed to fetch ${iconPath}`);
      return res.text();
    })
    .then((body) => { text.set(iconPath, body); inflight.delete(iconPath); return body; })
    .catch((err) => { inflight.delete(iconPath); throw err; });
  inflight.set(iconPath, p);
  return p;
}

const Icon = ({ name, size = 24, color = 'currentColor', style = {}, className = '', ...props }) => {
  // Seeded from the cache, so an icon this session has already seen is inline
  // on the first render rather than after a round trip.
  const cached = text.get(iconPaths[name]) ?? null;
  const [svgContent, setSvgContent] = useState(cached);
  const [loading, setLoading] = useState(cached === null);

  useEffect(() => {
    const iconPath = iconPaths[name];

    if (!iconPath) {
      console.warn(`Icon "${name}" not found in iconPaths`);
      setLoading(false);
      return undefined;
    }

    const hit = text.get(iconPath);
    if (hit !== undefined) { setSvgContent(hit); setLoading(false); return undefined; }

    let alive = true;
    setLoading(true);
    loadIcon(iconPath)
      .then((body) => { if (alive) { setSvgContent(body); setLoading(false); } })
      .catch((err) => {
        console.error(`Failed to load icon: ${name}`, err);
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [name]);

  const iconPath = iconPaths[name];

  if (!iconPath) {
    return null;
  }

  // If we have SVG content, render it inline for color control
  if (svgContent && !loading) {
    try {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
      const svgElement = svgDoc.documentElement;
      
      // Check for parsing errors
      const parserError = svgDoc.querySelector('parsererror');
      if (parserError) {
        throw new Error('SVG parsing failed');
      }
      
      // Update attributes
      svgElement.setAttribute('width', size);
      svgElement.setAttribute('height', size);
      svgElement.removeAttribute('fill');
      
      // Update fill in all paths (including paths that inherited fill from root <svg>)
      const paths = svgElement.querySelectorAll('path');
      const resolvedFill = color === 'currentColor' ? 'currentColor' : color;
      paths.forEach(path => {
        const currentFill = path.getAttribute('fill');
        if (currentFill === 'none') return;
        path.setAttribute('fill', resolvedFill);
      });

      // Apply style
      const styleAttr = Object.entries(style)
        .map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v}`)
        .join('; ');
      if (styleAttr) {
        svgElement.setAttribute('style', styleAttr);
      }
      
      if (className) {
        svgElement.setAttribute('class', className);
      }

      return (
        <span
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: color !== 'currentColor' ? color : undefined,
            width: size,
            height: size,
            ...style 
          }}
          className={className}
          {...props}
          dangerouslySetInnerHTML={{ __html: svgElement.outerHTML }}
        />
      );
    } catch (error) {
      console.error(`Error rendering SVG for icon: ${name}`, error);
    }
  }

  // Still loading: hold the space, ask for nothing.
  //
  // An <img> here rendered the same file the fetch was already asking for, so
  // every icon's FIRST appearance cost two requests — 20 for the ten in the
  // sidebar, measured in production on 2026-09-25. The second copy bought
  // roughly one frame of a grey, uncoloured glyph before the inline SVG
  // replaced it, and paid a round trip for it.
  //
  // Reserving the box keeps the layout from shifting when the SVG lands,
  // which is what the <img> was really providing.
  if (loading) {
    return (
      <span
        aria-hidden="true"
        style={{ display: 'inline-block', width: size, height: size, flexShrink: 0, ...style }}
        className={className}
        {...props}
      />
    );
  }

  // The fetch failed, or the SVG would not parse. Let the browser try the
  // file directly — it may well succeed where fetch did not (a blocked
  // extension, a transient network error), and a visible icon beats a gap.
  return (
    <img
      src={iconPath}
      alt={name}
      width={size}
      height={size}
      style={{
        display: 'inline-block',
        ...style
      }}
      className={className}
      {...props}
    />
  );
};

export default Icon;
